import { useState, useEffect, useRef, useCallback } from "react";
import { Shield, ShieldOff, Zap, AlertTriangle, CheckCircle, XCircle, Trash2, RefreshCw, Wifi, WifiOff } from "lucide-react";

const DEFAULT_GATEWAY = "http://localhost:9443";

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmtTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString("en-US", { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" }); }
  catch { return iso.slice(11, 19) || "—"; }
};

const verdictStyle = (v) => {
  if (v === "APPROVED") return "text-emerald-400 bg-emerald-900/30 border-emerald-700";
  if (v === "DENIED")   return "text-red-400    bg-red-900/40    border-red-600";
  if (v === "REVOKED")  return "text-orange-400 bg-orange-900/30 border-orange-600";
  return "text-slate-400 bg-slate-800/30 border-slate-600";
};

// ── Session card ──────────────────────────────────────────────────────────────
function SessionCard({ id, session, onRevoke }) {
  const isLegacy  = session.agent_id?.includes("LEGACY");
  const isActive  = session.active;
  const srcLabel  = session.entropy_source || "—";
  const isQuantum = srcLabel.includes("QRaaS");

  return (
    <div
      className={`border rounded-lg p-4 transition-all duration-500 ${
        !isActive
          ? "border-orange-700 bg-orange-950/20"
          : isLegacy
          ? "border-red-800 bg-red-950/20"
          : "border-cyan-700 bg-cyan-950/10"
      }`}
      data-testid={`session-card-${id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          {isLegacy
            ? <ShieldOff className="w-5 h-5 text-red-400" />
            : <Shield className="w-5 h-5 text-cyan-400" />}
          <div>
            <div className={`text-sm font-bold ${isLegacy ? "text-red-300" : "text-cyan-300"}`}>
              {session.agent_id}
            </div>
            <div className="text-[11px] text-slate-500 font-mono">{id}</div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${
            !isActive
              ? "text-orange-400 border-orange-600 bg-orange-950/40"
              : "text-emerald-400 border-emerald-700 bg-emerald-950/30"
          }`}>
            {isActive ? "ACTIVE" : "REVOKED"}
          </span>
          {isActive && (
            <button
              onClick={() => onRevoke(id)}
              className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold bg-red-900/50 border border-red-700 text-red-300 rounded hover:bg-red-800/60 transition-colors"
              data-testid={`revoke-btn-${id}`}
            >
              <Zap className="w-3 h-3" /> REVOKE
            </button>
          )}
        </div>
      </div>

      {/* Entropy source */}
      <div className="flex items-center gap-1.5 mb-2">
        <span className={`w-2 h-2 rounded-full ${isQuantum ? "bg-cyan-400 animate-pulse" : "bg-amber-400"}`} />
        <span className={`text-xs font-mono ${isQuantum ? "text-cyan-400" : "text-amber-400"}`}>
          {srcLabel}
        </span>
      </div>

      {/* Scopes */}
      <div className="flex flex-wrap gap-1 mb-2">
        {(session.allowed_scopes || []).map((s) => (
          <span key={s} className="text-[10px] px-1.5 py-0.5 bg-slate-800 border border-slate-700 text-slate-400 rounded font-mono">
            {s}
          </span>
        ))}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-800">
        {[
          ["Approved", session.action_count ?? 0, "text-emerald-400"],
          ["Denied",   session.deny_count   ?? 0, "text-red-400"],
          ["Started",  fmtTime(session.created), "text-slate-400"],
        ].map(([label, val, cls]) => (
          <div key={label} className="text-center">
            <div className="text-[9px] uppercase tracking-widest text-slate-500">{label}</div>
            <div className={`text-sm font-bold font-mono ${cls}`}>{val}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Audit row ─────────────────────────────────────────────────────────────────
function AuditRow({ entry }) {
  return (
    <div className={`grid grid-cols-[80px_1fr_100px_80px_120px] gap-1 px-3 py-2 border-b border-slate-800/60 text-[11px] font-mono ${
      entry.verdict === "DENIED"  ? "bg-red-950/20"
      : entry.verdict === "REVOKED" ? "bg-orange-950/20"
      : ""
    }`} data-testid="audit-row">
      <div className="text-slate-500">{fmtTime(entry.timestamp)}</div>
      <div className="text-slate-300 truncate">{entry.action} → <span className="text-slate-500">{entry.target}</span></div>
      <div className="text-slate-400 truncate">{entry.scope}</div>
      <div className={`text-[10px] font-mono`}>
        {entry.entropy_ref && entry.entropy_ref !== "—"
          ? <span className="text-cyan-600">{entry.entropy_ref.slice(0, 8)}…</span>
          : <span className="text-slate-600">—</span>}
      </div>
      <div className={`text-center`}>
        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${verdictStyle(entry.verdict)}`}>
          {entry.verdict}
        </span>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function QShieldDemo() {
  const [gatewayUrl, setGatewayUrl]   = useState(DEFAULT_GATEWAY);
  const [inputUrl,   setInputUrl]     = useState(DEFAULT_GATEWAY);
  const [data,       setData]         = useState(null);
  const [connected,  setConnected]    = useState(false);
  const [alert,      setAlert]        = useState(null);  // { agent_id, action, scope }
  const [alertVisible, setAlertVisible] = useState(false);
  const prevDeniesRef = useRef(0);
  const auditRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const r = await fetch(`${gatewayUrl}/dashboard-data`, { signal: AbortSignal.timeout(2000) });
      if (!r.ok) throw new Error("not ok");
      const d = await r.json();
      setData(d);
      setConnected(true);

      // Detect new violations
      const violations = (d.audit || []).filter((e) => e.verdict === "DENIED");
      if (violations.length > prevDeniesRef.current) {
        const latest = violations[violations.length - 1];
        setAlert({ agent_id: latest.agent_id, action: latest.action, scope: latest.scope });
        setAlertVisible(true);
        prevDeniesRef.current = violations.length;
      }
    } catch {
      setConnected(false);
    }
  }, [gatewayUrl]);

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 500);
    return () => clearInterval(id);
  }, [fetchData]);

  // Auto-hide alert after 8 seconds
  useEffect(() => {
    if (!alertVisible) return;
    const t = setTimeout(() => setAlertVisible(false), 8000);
    return () => clearTimeout(t);
  }, [alertVisible]);

  const handleRevoke = async (sessionId) => {
    try {
      await fetch(`${gatewayUrl}/sessions/${sessionId}`, { method: "DELETE" });
    } catch (e) {
      alert(`Revoke failed: ${e.message}`);
    }
  };

  const handleReset = async () => {
    try {
      await fetch(`${gatewayUrl}/reset`, { method: "POST" });
      prevDeniesRef.current = 0;
      setAlert(null);
      setAlertVisible(false);
    } catch (e) {
      alert(`Reset failed: ${e.message}`);
    }
  };

  const sessions = data?.sessions || {};
  const audit    = (data?.audit || []).slice().reverse();
  const stats    = data?.stats || {};

  return (
    <div className="min-h-screen bg-[#0d1117] text-[#e8ecf2] flex flex-col" data-testid="qshield-demo-page">

      {/* ── Header ── */}
      <div className="bg-[#080c14] border-b border-slate-800 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="w-6 h-6 text-cyan-400" />
          <div>
            <h1 className="text-xl font-bold text-white tracking-wide">QCS-Shield Governance Dashboard</h1>
            <p className="text-xs text-slate-500">Real-time AI agent activity · Scope enforcement · Kill switch</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Gateway URL input */}
          <div className="flex items-center gap-2 bg-slate-900 border border-slate-700 rounded px-3 py-1.5">
            {connected
              ? <Wifi className="w-3.5 h-3.5 text-emerald-400" />
              : <WifiOff className="w-3.5 h-3.5 text-red-400" />}
            <input
              value={inputUrl}
              onChange={(e) => setInputUrl(e.target.value)}
              onBlur={() => setGatewayUrl(inputUrl)}
              onKeyDown={(e) => e.key === "Enter" && setGatewayUrl(inputUrl)}
              className="bg-transparent text-xs text-slate-300 font-mono outline-none w-52"
              placeholder="http://localhost:9443"
              data-testid="gateway-url-input"
            />
          </div>

          <button
            onClick={handleReset}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-slate-700 text-slate-400 rounded hover:bg-slate-800 transition-colors"
            data-testid="reset-btn"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Reset Demo
          </button>
        </div>
      </div>

      {/* ── Policy Violation Alert ── */}
      {alertVisible && alert && (
        <div
          className="mx-6 mt-4 border-2 border-red-600 bg-red-950/40 rounded-lg px-6 py-4 flex items-center justify-between animate-pulse"
          data-testid="violation-alert"
        >
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-7 h-7 text-red-400 shrink-0" />
            <div>
              <div className="text-lg font-black text-red-300 tracking-wide">POLICY VIOLATION DETECTED</div>
              <div className="text-sm text-red-400 font-mono">
                Agent <span className="text-red-200">{alert.agent_id}</span> attempted &nbsp;
                <span className="text-red-200">{alert.action}</span> &nbsp;—&nbsp;
                scope <span className="text-red-200 font-bold">{alert.scope}</span> rejected
              </div>
            </div>
          </div>
          <button onClick={() => setAlertVisible(false)} className="text-red-600 hover:text-red-400 transition-colors">
            <XCircle className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* ── Connection status ── */}
      {!connected && (
        <div className="mx-6 mt-4 border border-amber-700 bg-amber-950/20 rounded px-4 py-3 text-sm text-amber-400 font-mono" data-testid="disconnected-banner">
          Gateway not reachable at <span className="font-bold">{gatewayUrl}</span> — start <code>gateway.py</code> and check the URL above.
        </div>
      )}

      <div className="flex-1 flex flex-col gap-4 p-6">

        {/* ── KPI strip ── */}
        <div className="grid grid-cols-4 gap-3" data-testid="kpi-strip">
          {[
            ["Sessions",       stats.total_sessions    ?? 0, "text-cyan-400"],
            ["Active",         stats.active_sessions   ?? 0, "text-emerald-400"],
            ["Verifications",  stats.total_verifications ?? 0, "text-slate-300"],
            ["Violations",     stats.violations        ?? 0, "text-red-400"],
          ].map(([label, val, cls]) => (
            <div key={label} className="bg-slate-900/60 border border-slate-800 rounded-lg p-4 text-center">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
              <div className={`text-3xl font-black font-mono ${cls}`}>{val}</div>
            </div>
          ))}
        </div>

        <div className="flex gap-4 flex-1 min-h-0">

          {/* ── Sessions panel ── */}
          <div className="w-80 shrink-0 flex flex-col gap-3" data-testid="sessions-panel">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
              Active Sessions ({Object.keys(sessions).length})
            </div>

            {Object.keys(sessions).length === 0 ? (
              <div className="flex-1 flex items-center justify-center text-slate-600 text-sm">
                No sessions registered yet.<br />Start the agents.
              </div>
            ) : (
              <div className="flex flex-col gap-3 overflow-y-auto">
                {Object.entries(sessions).map(([id, s]) => (
                  <SessionCard key={id} id={id} session={s} onRevoke={handleRevoke} />
                ))}
              </div>
            )}
          </div>

          {/* ── Audit log ── */}
          <div className="flex-1 flex flex-col min-h-0 border border-slate-800 rounded-lg bg-slate-900/30 overflow-hidden">
            {/* Table header */}
            <div className="grid grid-cols-[80px_1fr_100px_80px_120px] gap-1 px-3 py-2.5 bg-slate-800/60 border-b border-slate-700 shrink-0">
              {["Time", "Action → Target", "Scope", "Entropy", "Verdict"].map((h) => (
                <div key={h} className="text-[9px] font-bold uppercase tracking-widest text-slate-400">{h}</div>
              ))}
            </div>

            <div ref={auditRef} className="flex-1 overflow-y-auto" data-testid="audit-feed">
              {audit.length === 0 ? (
                <div className="flex items-center justify-center h-full text-slate-600 text-sm">
                  No events yet — run the demo.
                </div>
              ) : (
                audit.map((entry, i) => <AuditRow key={i} entry={entry} />)
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
